import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { AquaculturePermit, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const SPECIES = ['PRAWN', 'TILAPIA', 'SEA_CUCUMBER', 'GIANT_CLAM', 'RABBITFISH', 'MILKFISH'] as const;

/** M32 Fisheries — aquaculture site permits: GeoServe marine spatial check + M31 EIA trigger. */
export function AquaculturePermits() {
  const { persona } = useAuth();
  const isLicensing = persona ? persona.roles.includes('SFA_LICENSING') : false;
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m32/aquaculture-permits?${params.toString()}`;
  }, [status, appliedQ]);
  const listQ = useApiQuery<Page<AquaculturePermit>>(path);

  return (
    <div>
      <PageHeader
        title="Aquaculture Permits"
        subtitle="Site permits — live GeoServe marine spatial check, EIA referral for large sites (M32)."
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New permit application
          </button>
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setAppliedQ(q);
          }}
        >
          <input className="field-input !w-56" placeholder="Permit no, site name…" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {['APPLIED', 'SITE_CHECK', 'EIA_REFERRED', 'FEE_PENDING', 'ACTIVE', 'SUSPENDED', 'REVOKED', 'EXPIRED', 'REJECTED', 'WITHDRAWN'].map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
          <button type="submit" className="btn-secondary">
            Search
          </button>
        </form>
      </div>

      {!isLicensing ? <RoleNotice roles={['SFA_LICENSING']} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No aquaculture permits match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Permit No</th>
                <th className="table-head">Site</th>
                <th className="table-head">Species</th>
                <th className="table-head">Capacity/yr</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((p) => (
                <tr key={p.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(p.id)}>
                  <td className="table-cell font-mono text-xs font-medium">{p.permitNo}</td>
                  <td className="table-cell">
                    {p.siteName}
                    <div className="text-[11px] text-slate-400">{p.siteDistrict ?? '—'}</div>
                  </td>
                  <td className="table-cell text-xs">{p.species.replaceAll('_', ' ')}</td>
                  <td className="table-cell text-xs">{Number(p.productionCapacityKgPerYear).toLocaleString()} kg</td>
                  <td className="table-cell">
                    <StatusBadge status={p.status} />
                    {p.eiaRequired ? <div className="mt-1 text-[10px] font-semibold text-amber-600">EIA required</div> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId ? (
        <PermitDetailModal
          id={selectedId}
          isLicensing={isLicensing}
          onClose={() => setSelectedId(null)}
          onChanged={() => {
            setSelectedId(null);
            listQ.reload();
          }}
        />
      ) : null}
      {showCreate ? (
        <NewPermitModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function PermitDetailModal({ id, isLicensing, onClose, onChanged }: { id: string; isLicensing: boolean; onClose: () => void; onChanged: () => void }) {
  const pq = useApiQuery<AquaculturePermit>(`/api/v1/m32/aquaculture-permits/${id}`);
  const p = pq.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDecision, setShowDecision] = useState<'eia' | 'approve' | 'reject' | 'suspend' | 'revoke' | null>(null);
  const [note, setNote] = useState('');

  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };
  const reinstate = () => call(() => api.post(`/api/v1/m32/aquaculture-permits/${id}/reinstate`));
  const confirm = () =>
    call(() => {
      if (showDecision === 'eia') return api.post(`/api/v1/m32/aquaculture-permits/${id}/eia-clearance`, { note });
      if (showDecision === 'approve') return api.post(`/api/v1/m32/aquaculture-permits/${id}/review`, { approve: true, note: note || undefined });
      if (showDecision === 'reject') return api.post(`/api/v1/m32/aquaculture-permits/${id}/review`, { approve: false, rejectionReason: note });
      if (showDecision === 'suspend') return api.post(`/api/v1/m32/aquaculture-permits/${id}/suspend`, { reason: note });
      return api.post(`/api/v1/m32/aquaculture-permits/${id}/revoke`, { reason: note });
    });

  return (
    <Modal title={p ? `${p.permitNo} — ${p.siteName}` : 'Aquaculture permit'} onClose={onClose} wide>
      {pq.error ? <ErrorNote message={pq.error} /> : null}
      {pq.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {p ? (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="Status" value={<StatusBadge status={p.status} />} />
            <Detail label="Operator" value={`${p.operatorType}:${p.operatorId}`} mono />
            <Detail label="Species" value={p.species.replaceAll('_', ' ')} />
            <Detail label="Capacity/yr" value={`${Number(p.productionCapacityKgPerYear).toLocaleString()} kg`} />
            <Detail label="District" value={p.siteDistrict ?? '—'} />
            <Detail label="Spatial check" value={formatDateTime(p.spatialCheckAt)} />
            <Detail label="EIA required" value={p.eiaRequired ? 'Yes' : 'No'} />
            <Detail label="EIA cleared" value={formatDateTime(p.eiaClearedAt)} />
            <Detail label="Valid" value={`${formatDate(p.validFrom)} → ${formatDate(p.validTo)}`} />
          </div>
          {p.rejectionReason ? <ErrorNote message={`Rejected: ${p.rejectionReason}`} /> : null}
          {p.suspendedReason ? <ErrorNote message={`Suspended: ${p.suspendedReason}`} /> : null}
          {p.revokedReason ? <ErrorNote message={`Revoked: ${p.revokedReason}`} /> : null}

          {isLicensing ? (
            <div className="border-t border-slate-100 pt-4">
              {!showDecision ? (
                <div className="flex flex-wrap gap-2">
                  {p.status === 'EIA_REFERRED' ? (
                    <button className="btn-success" disabled={busy} onClick={() => setShowDecision('eia')}>
                      Record M31 EIA clearance
                    </button>
                  ) : null}
                  {p.status === 'SITE_CHECK' ? (
                    <>
                      <button className="btn-success" disabled={busy} onClick={() => setShowDecision('approve')}>
                        Approve (open fee intent)
                      </button>
                      <button className="btn-danger" disabled={busy} onClick={() => setShowDecision('reject')}>
                        Reject
                      </button>
                    </>
                  ) : null}
                  {p.status === 'ACTIVE' ? (
                    <button className="btn-secondary" disabled={busy} onClick={() => setShowDecision('suspend')}>
                      Suspend
                    </button>
                  ) : null}
                  {p.status === 'SUSPENDED' ? (
                    <button className="btn-success" disabled={busy} onClick={reinstate}>
                      Reinstate
                    </button>
                  ) : null}
                  {p.status === 'ACTIVE' || p.status === 'SUSPENDED' ? (
                    <button className="btn-danger" disabled={busy} onClick={() => setShowDecision('revoke')}>
                      Revoke
                    </button>
                  ) : null}
                </div>
              ) : (
                <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                  <Field label={showDecision === 'reject' || showDecision === 'suspend' || showDecision === 'revoke' ? 'Reason' : 'Note'}>
                    <textarea className="field-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
                  </Field>
                  <div className="flex gap-2">
                    <button className="btn-secondary" onClick={() => setShowDecision(null)}>
                      Cancel
                    </button>
                    <button className={showDecision === 'approve' || showDecision === 'eia' ? 'btn-success' : 'btn-danger'} disabled={busy || note.trim().length < 3} onClick={confirm}>
                      {busy ? 'Submitting…' : 'Confirm'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

function NewPermitModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [operatorType, setOperatorType] = useState<'PERSON' | 'ENTITY'>('ENTITY');
  const [operatorId, setOperatorId] = useState('');
  const [siteName, setSiteName] = useState('');
  const [siteDistrict, setSiteDistrict] = useState('');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [species, setSpecies] = useState<(typeof SPECIES)[number]>('PRAWN');
  const [capacity, setCapacity] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m32/aquaculture-permits', {
        operatorType,
        operatorId,
        siteName,
        siteGeometry: { type: 'Point', coordinates: [Number.parseFloat(lng), Number.parseFloat(lat)] },
        siteDistrict: siteDistrict || undefined,
        species,
        productionCapacityKgPerYear: Number.parseFloat(capacity),
      });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New aquaculture permit application" onClose={onClose} wide>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Operator type">
            <select className="field-input" value={operatorType} onChange={(e) => setOperatorType(e.target.value as 'PERSON' | 'ENTITY')}>
              <option value="ENTITY">Entity</option>
              <option value="PERSON">Person</option>
            </select>
          </Field>
          <Field label={operatorType === 'ENTITY' ? 'Business ID' : 'NIN'}>
            <input className="field-input" value={operatorId} onChange={(e) => setOperatorId(e.target.value)} />
          </Field>
        </div>
        <Field label="Site name">
          <input className="field-input" value={siteName} onChange={(e) => setSiteName(e.target.value)} />
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Latitude">
            <input className="field-input" type="number" step="0.000001" value={lat} onChange={(e) => setLat(e.target.value)} placeholder="-4.6796" />
          </Field>
          <Field label="Longitude">
            <input className="field-input" type="number" step="0.000001" value={lng} onChange={(e) => setLng(e.target.value)} placeholder="55.4920" />
          </Field>
          <Field label="District (optional)">
            <input className="field-input" value={siteDistrict} onChange={(e) => setSiteDistrict(e.target.value)} placeholder="Anse Royale" />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Species">
            <select className="field-input" value={species} onChange={(e) => setSpecies(e.target.value as (typeof SPECIES)[number])}>
              {SPECIES.map((s) => (
                <option key={s} value={s}>
                  {s.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Production capacity (kg/yr)">
            <input className="field-input" type="number" value={capacity} onChange={(e) => setCapacity(e.target.value)} />
          </Field>
        </div>
        <button className="btn-primary w-full" disabled={busy || !operatorId || !siteName || !lat || !lng || !capacity} onClick={submit}>
          {busy ? 'Submitting…' : 'Submit application (runs GeoServe spatial check)'}
        </button>
      </div>
    </Modal>
  );
}
